import { hasPermission, type Permission } from '@bookora/shared';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { AuthUser, ClinicAccess, MeResult, Membership } from '@/api/types';
import { env } from '@/lib/env';

/**
 * The session, and the only place it lives.
 *
 * ── WHY localStorage AND NOT AN httpOnly COOKIE ─────────────────────────────
 *
 * The API is a separate origin behind a separate nginx vhost, the refresh token
 * is rotated on every use with reuse detection on the server, and the panel is
 * served with `frame-ancestors 'none'` and a script-src that forbids inline
 * script. A cookie would buy XSS resistance we would then have to give back as
 * a CSRF surface across origins. The mitigations that matter here are the
 * server's: short access-token lifetime, rotation, reuse detection, and a
 * per-device session list the user can revoke.
 *
 * ── WHY PERMISSIONS ARE CACHED HERE ─────────────────────────────────────────
 *
 * `permissions` drives which buttons render. It is an AFFORDANCE CACHE, not an
 * authorisation decision — the backend re-resolves the caller's effective
 * permissions from the database on every single request and answers 403
 * regardless of what this array says. A stale array here can only produce a
 * button that fails with a Persian error, never an action that succeeds.
 * `/auth/me` refreshes it on boot, on window focus and after a clinic switch.
 */

export interface SessionState {
  accessToken: string | null;
  refreshToken: string | null;
  /** Epoch ms at which `accessToken` stops being accepted. */
  accessTokenExpiresAt: number | null;
  /** Device session id — the row marked «این دستگاه» in the session list. */
  sessionId: string | null;

  user: AuthUser | null;
  activeClinicId: string | null;
  memberships: Membership[];
  permissions: Permission[];
  /** Set when a super-admin is acting as this user; the shell shows a banner. */
  impersonatedBy: string | null;
  /**
   * Whether the active clinic accepts writes, from `/auth/me` — for every
   * role. `null` until known. Display only: the server's 402 is the gate.
   */
  activeClinicAccess: ClinicAccess | null;

  /** `false` until the persisted bundle has been read back from localStorage. */
  hydrated: boolean;
}

export interface IdentityInput {
  user: AuthUser;
  activeClinicId?: string | null;
  memberships?: Membership[];
  permissions?: string[];
  impersonatedBy?: string | null;
  activeClinicAccess?: ClinicAccess | null;
}

/**
 * `/auth/me` → the store's identity. The ONE mapping the boot, the login, the
 * signup and the clinic switch all use, so a field the server adds (the
 * clinic's write access was the latest) cannot reach three of them and miss
 * the fourth.
 */
export function identityFromMe(
  me: MeResult,
  fallbackClinicId: string | null = null,
): IdentityInput {
  return {
    user: me.user,
    activeClinicId: me.activeClinicId ?? fallbackClinicId,
    memberships: me.memberships ?? [],
    permissions: me.permissions ?? [],
    impersonatedBy: me.impersonatedBy ?? null,
    // Absent means the server could not compute it — keep whatever was known
    // rather than claiming «writable».
    ...(me.activeClinicAccess === undefined ? {} : { activeClinicAccess: me.activeClinicAccess }),
  };
}

interface SessionActions {
  setSession: (input: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    sessionId: string;
    user: AuthUser;
    activeClinicId?: string | null;
    memberships?: Membership[];
  }) => void;
  setTokens: (input: { accessToken: string; refreshToken: string; expiresIn?: number }) => void;
  /**
   * Install a super-admin impersonation token handed over in the URL fragment.
   *
   * Distinct from {@link setSession} because there is no user, no membership
   * list and no refresh token to install — `/auth/me` fills the first two on
   * the next tick and the third does not exist by design
   * (`ImpersonationService.start` mints an access token and nothing else, so
   * the session cannot renew itself). It REPLACES whatever session was in this
   * browser: merging would leave two identities in one store.
   *
   * `impersonatedBy` is stamped immediately, before the server has confirmed
   * anything, so there is no frame in which somebody is acting as another user
   * with no banner on screen. The server's answer overwrites it moments later.
   */
  beginImpersonation: (input: {
    accessToken: string;
    expiresIn: number;
    sessionId: string;
    activeClinicId: string;
  }) => void;
  setIdentity: (input: IdentityInput) => void;
  clearSession: () => void;
  markHydrated: () => void;
}

/**
 * Placeholder for `impersonatedBy` between the fragment handoff and the first
 * `/auth/me`. It only has to be truthy: the shell renders its banner on the
 * presence of a value, and the real super-admin's id replaces this one within
 * the same boot.
 */
export const IMPERSONATION_PENDING = 'pending';

const EMPTY: Omit<SessionState, 'hydrated'> = {
  accessToken: null,
  refreshToken: null,
  accessTokenExpiresAt: null,
  sessionId: null,
  user: null,
  activeClinicId: null,
  memberships: [],
  permissions: [],
  impersonatedBy: null,
  activeClinicAccess: null,
};

export const useSessionStore = create<SessionState & SessionActions>()(
  persist(
    (set) => ({
      ...EMPTY,
      hydrated: false,

      setSession: ({ accessToken, refreshToken, expiresIn, sessionId, user, ...rest }) =>
        set((state) => {
          const activeClinicId = rest.activeClinicId ?? null;
          return {
            accessToken,
            refreshToken,
            accessTokenExpiresAt: Date.now() + expiresIn * 1000,
            sessionId,
            user,
            activeClinicId,
            memberships: rest.memberships ?? [],
            // Another clinic's (or another user's) access says nothing about
            // this one; `/auth/me` fills it moments later.
            activeClinicAccess:
              state.activeClinicAccess?.clinicId === activeClinicId
                ? state.activeClinicAccess
                : null,
          };
        }),

      setTokens: ({ accessToken, refreshToken, expiresIn }) =>
        set({
          accessToken,
          refreshToken,
          // `null`, not the previous value, when the caller does not know the
          // lifetime: the transport layer in `@bookora/ui` rotates the pair
          // without reading `expiresIn` off the response, and an expiry stamp
          // describing a token that no longer exists is worse than no stamp —
          // the first thing to read it would decide the session was still fresh
          // for another fifteen minutes.
          accessTokenExpiresAt: expiresIn === undefined ? null : Date.now() + expiresIn * 1000,
        }),

      setIdentity: ({
        user,
        activeClinicId,
        memberships,
        permissions,
        impersonatedBy,
        activeClinicAccess,
      }) =>
        set((state) => ({
          user,
          activeClinicId: activeClinicId === undefined ? state.activeClinicId : activeClinicId,
          memberships: memberships ?? state.memberships,
          permissions: (permissions ?? state.permissions) as Permission[],
          impersonatedBy: impersonatedBy === undefined ? state.impersonatedBy : impersonatedBy,
          activeClinicAccess:
            activeClinicAccess === undefined ? state.activeClinicAccess : activeClinicAccess,
        })),

      beginImpersonation: ({ accessToken, expiresIn, sessionId, activeClinicId }) =>
        set({
          ...EMPTY,
          accessToken,
          refreshToken: null,
          accessTokenExpiresAt: Date.now() + expiresIn * 1000,
          sessionId,
          activeClinicId,
          impersonatedBy: IMPERSONATION_PENDING,
        }),

      clearSession: () => set({ ...EMPTY }),

      markHydrated: () => set({ hydrated: true }),
    }),
    {
      name: env.tokenStorageKey,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        accessTokenExpiresAt: state.accessTokenExpiresAt,
        sessionId: state.sessionId,
        user: state.user,
        activeClinicId: state.activeClinicId,
        memberships: state.memberships,
        permissions: state.permissions,
        impersonatedBy: state.impersonatedBy,
        // Persisted so a reload into a read-only clinic shows its banner on
        // the first paint; the boot's `/auth/me` replaces it at once.
        activeClinicAccess: state.activeClinicAccess,
      }),
      onRehydrateStorage: () => (state) => state?.markHydrated(),
    },
  ),
);

/* -------------------------------------------------------------------------- */
/* Non-hook accessors — used inside the axios interceptors                    */
/* -------------------------------------------------------------------------- */

export function getSessionSnapshot(): SessionState {
  return useSessionStore.getState();
}

export function setTokens(input: {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
}): void {
  useSessionStore.getState().setTokens(input);
}

export function clearSession(): void {
  useSessionStore.getState().clearSession();
}

/**
 * Fired by the mutator when a refresh fails or a token is rejected outright.
 * `AuthProvider` listens and navigates to the login screen; using an event
 * rather than a direct import keeps the axios layer free of router knowledge.
 */
export const AUTH_EXPIRED_EVENT = 'bookora:auth-expired';

export function broadcastAuthExpired(reason: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { reason } }));
}

/* -------------------------------------------------------------------------- */
/* Selectors                                                                  */
/* -------------------------------------------------------------------------- */

export interface AuthSnapshot {
  user: AuthUser | null;
  activeClinicId: string | null;
  activeClinic: Membership | null;
  memberships: Membership[];
  permissions: Permission[];
  impersonatedBy: string | null;
  /** The ACTIVE clinic's write access, or `null` when unknown. */
  activeClinicAccess: ClinicAccess | null;
  hydrated: boolean;
  isAuthenticated: boolean;
  /** `true` when this account belongs to more than one clinic. */
  canSwitchClinic: boolean;
}

export function useAuth(): AuthSnapshot {
  const user = useSessionStore((s) => s.user);
  const accessToken = useSessionStore((s) => s.accessToken);
  const activeClinicId = useSessionStore((s) => s.activeClinicId);
  const memberships = useSessionStore((s) => s.memberships);
  const permissions = useSessionStore((s) => s.permissions);
  const impersonatedBy = useSessionStore((s) => s.impersonatedBy);
  const access = useSessionStore((s) => s.activeClinicAccess);
  const hydrated = useSessionStore((s) => s.hydrated);

  return {
    user,
    activeClinicId,
    activeClinic: memberships.find((m) => m.clinicId === activeClinicId) ?? null,
    memberships,
    permissions,
    impersonatedBy,
    // Never another clinic's answer, in the moment between a switch and the
    // `/auth/me` that follows it.
    activeClinicAccess: access && access.clinicId === activeClinicId ? access : null,
    hydrated,
    isAuthenticated: Boolean(accessToken && user),
    canSwitchClinic: memberships.length > 1,
  };
}

/**
 * Permission check against the cached list, for code outside React.
 *
 * Inside components use `useCan()` — it subscribes, so a clinic switch
 * re-renders the affected affordances instead of leaving stale ones on screen.
 */
export function currentUserCan(permission: Permission): boolean {
  return hasPermission(useSessionStore.getState().permissions, permission);
}
